import type { SiteLocationConfig, StatusDashboardConfig } from '../types'
import { defaultRunwayLayoutsFor } from './resolveWidget'
import { createDefaultDashboardTabs } from './tabCatalogue'
import type { StatusDashboardSettings, WidgetSettings } from './types'
import type { WidgetDefinition } from './widgetCatalogue'
import {
    DASHBOARD_WIDGETS,
    DashboardWidgetId,
    RunwaysOption,
    WeatherOption,
} from './widgetCatalogue'

const HIDDEN = 'HIDDEN'
const NO_CLOSURE = 'NO_CLOSURE'
const DEFAULT = 'DEFAULT'

export type LegacyStatusDashboardConfig = StatusDashboardConfig & {
    showWeatherBarsForNetworkAirports?: boolean
}

type LegacyWidget = LegacyStatusDashboardConfig['widgets'][string]

const flavorAt = (legacy: LegacyWidget, airport: string): string =>
    legacy.flavorOverride?.[airport] ?? legacy.flavor ?? DEFAULT

const isOptionOn = (
    definition: WidgetDefinition,
    optionKey: string,
    legacy: LegacyWidget,
    visibleAirports: string[],
    showWeatherBarsForNetworkAirports: boolean,
): boolean => {
    if (definition.id === DashboardWidgetId.Runways && optionKey === RunwaysOption.ClosurePlan) {
        return visibleAirports.length > 0
            ? visibleAirports.some(airport => flavorAt(legacy, airport) !== NO_CLOSURE)
            : (legacy.flavor ?? DEFAULT) !== NO_CLOSURE
    }
    if (
        definition.id === DashboardWidgetId.Weather &&
        optionKey === WeatherOption.NetworkAirportProbabilityBars
    ) {
        return showWeatherBarsForNetworkAirports
    }
    return !(legacy.hiddenSubsections ?? []).includes(optionKey)
}

const convertListedWidget = (
    definition: WidgetDefinition,
    legacy: LegacyWidget,
    airports: string[],
    showWeatherBarsForNetworkAirports: boolean,
): WidgetSettings => {
    const visibleAirports = airports.filter(airport => flavorAt(legacy, airport) !== HIDDEN)
    return {
        airports: visibleAirports,
        options: Object.fromEntries(
            definition.options.map(option => [
                option.key,
                isOptionOn(
                    definition,
                    option.key,
                    legacy,
                    visibleAirports,
                    showWeatherBarsForNetworkAirports,
                ),
            ]),
        ),
    }
}

const convertWidget = (
    definition: WidgetDefinition,
    legacy: LegacyWidget | undefined,
    airports: string[],
    showWeatherBarsForNetworkAirports: boolean,
): WidgetSettings => {
    if (!legacy && definition.optIn) {
        return { airports: [], options: {} }
    }
    return convertListedWidget(
        definition,
        legacy ?? {},
        airports,
        showWeatherBarsForNetworkAirports,
    )
}

export const fromLegacyStatusDashboardConfig = (
    legacy: LegacyStatusDashboardConfig | undefined,
    airportDetails: Record<string, unknown> = {},
): StatusDashboardSettings => {
    const airports = Object.keys(airportDetails)
    const showWeatherBars = legacy?.showWeatherBarsForNetworkAirports ?? false
    return {
        allowAdminDismissWarning: legacy?.allowAdminDismissWarning ?? false,
        tabs: createDefaultDashboardTabs(airports),
        widgets: Object.fromEntries(
            DASHBOARD_WIDGETS.map(definition => [
                definition.id,
                convertWidget(
                    definition,
                    legacy?.widgets?.[definition.id],
                    airports,
                    showWeatherBars,
                ),
            ]),
        ),
    }
}

type SiteLocationDashboardKeys = Partial<
    Pick<SiteLocationConfig, 'statusDashboard' | 'statusDashboardConfig' | 'airports'>
>

const withMissingDefaults = (
    settings: StatusDashboardSettings,
    airports: string[],
): StatusDashboardSettings =>
    settings.tabs && settings.runwayLayouts
        ? settings
        : {
              ...settings,
              tabs: settings.tabs ?? createDefaultDashboardTabs(airports),
              runwayLayouts: settings.runwayLayouts ?? defaultRunwayLayoutsFor(airports),
          }

const resolvedSettings = new WeakMap<SiteLocationDashboardKeys, StatusDashboardSettings>()

export const statusDashboardSettingsOf = (
    siteLocation: SiteLocationDashboardKeys | undefined,
): StatusDashboardSettings | undefined => {
    if (!siteLocation?.statusDashboard && !siteLocation?.statusDashboardConfig) {
        return undefined
    }
    const cached = resolvedSettings.get(siteLocation)
    if (cached) {
        return cached
    }
    const airports = Object.keys(siteLocation.airports ?? {})
    const settings = withMissingDefaults(
        siteLocation.statusDashboard ??
            fromLegacyStatusDashboardConfig(
                siteLocation.statusDashboardConfig,
                siteLocation.airports,
            ),
        airports,
    )
    resolvedSettings.set(siteLocation, settings)
    return settings
}
